// React imports first
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Third-party library imports next
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import {
  SettingsRounded,
  DashboardRounded,
  TranslateRounded,
  InterpreterModeRounded,
  RecordVoiceOverRounded,
  PeopleRounded,
  GroupsRounded,
  RequestPageRounded,
  PriceCheckRounded,
  PointOfSaleRounded,
  AssessmentRounded,
} from "@mui/icons-material";
import MenuOpenRoundedIcon from "@mui/icons-material/MenuOpenRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import HelpCenterRoundedIcon from "@mui/icons-material/HelpCenterRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";

// Local imports last
import FlexBetween from "./FlexBetween";
import { useStateContext } from "../contexts/ContextProvider";
import Chip from "@mui/material/Chip";
import api from "../services/api";
import { useWebSocket } from "../contexts/WebSocketContext";

// Constants
const ANIMATION_SPEED = 50;

// Styled HtmlTooltip component
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.background.alt
        : theme.palette.background.paper,
    color: theme.palette.primary.main,
    maxWidth: 340,
    boxShadow: theme.shadows[1],
    fontSize: theme.typography.pxToRem(14),
    padding: "8px 12px",
  },
}));

// Custom menu item component
const MenuItem = ({
  icon,
  text,
  active,
  link,
  onClick,
  isCollapsed,
  textVisible,
  badgeCount,
}) => {
  const theme = useTheme();
  const isActive = active === link;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        borderRadius: "12px",
        mx: isCollapsed ? 1.25 : 2,
        my: 0.25,
        py: 1,
        px: 1.25,
        backgroundColor: isActive ? theme.palette.primary.main : "transparent",
        color: isActive
          ? theme.palette.primary.main
          : theme.palette.primary.text2,
        transition: "background-color 0.3s ease",
        "&:hover": {
          backgroundColor: isActive
            ? theme.palette.primary.buttonHoverSelected
            : theme.palette.primary.buttonHover,
        },
      }}
    >
      {/* Icon container - fixed width to maintain position */}
      <Box
        sx={{
          width: "40px",
          height: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "flex-start",
          color: isActive
            ? theme.palette.background.default
            : theme.palette.primary.text2,
        }}
      >
        {icon}
      </Box>

      {/* Text container with fade effect */}
      {!isCollapsed && (
        <Box
          sx={{
            ml: 1,
            flexGrow: 1,
            opacity: textVisible ? 1 : 0,
            transition: `opacity ${ANIMATION_SPEED}ms ease-in-out`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: isActive
                ? theme.palette.background.default
                : theme.palette.primary.text2,
            }}
          >
            {text}
          </Typography>
          {badgeCount > 0 && (
            <Chip
              label={badgeCount}
              size="small"
              sx={{
                height: "20px",
                borderRadius: "8px",
                fontSize: "0.75rem",
                fontWeight: "bold",
                bgcolor: isActive
                  ? theme.palette.background.default
                  : theme.palette.primary.main,
                color: isActive
                  ? theme.palette.primary.main
                  : theme.palette.background.default,
                minWidth: "20px",
                "& .MuiChip-label": {
                  px: 0.75,
                },
              }}
            />
          )}
          {/* Removed arrow from selected items */}
        </Box>
      )}
    </Box>
  );
};

// Custom section header component
const SectionHeader = ({ text, isCollapsed, textVisible }) => {
  const theme = useTheme();

  if (isCollapsed) return null;

  return (
    <Box
      sx={{
        mx: 2,
        mt: 0.5, // Reduced from 2 to 1 (half spacing)
        mb: 0.125, // Reduced from 0.5 to 0.25 (half spacing)
        opacity: textVisible ? 1 : 0,
        transition: `opacity ${ANIMATION_SPEED}ms ease-in-out`,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.primary.main,
          fontWeight: "bold",
          fontSize: 13,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

const Sidebar = ({ user, drawerWidth, isNonMobile }) => {
  // Define the collapsed drawer width (exactly 25% of full width)
  const collapsedDrawerWidth = Math.floor(drawerWidth * 0.25);

  // Use the context for sidebar state management
  const {
    activeMenu,
    setActiveMenu,
    isCollapsed: contextIsCollapsed,
    setIsCollapsed: setContextIsCollapsed,
  } = useStateContext();

  // Get theme
  const theme = useTheme();

  // WebSocket instance (provided by WebSocketProvider)
  const { socket } = useWebSocket();

  // Local state for task count
  const [taskCount, setTaskCount] = useState(0);
  // Local state for the unbilled-assignments badge (Invoices item)
  const [unbilledCount, setUnbilledCount] = useState(0);

  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Set mounted ref on mount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch task count from API
  const fetchTaskCount = useCallback(async () => {
    try {
      const userId = user?.id;
      if (!userId) {
        return;
      }

      const params = new URLSearchParams();
      params.append("memberId", userId);
      params.append("hideAutomatedTasks", "false");

      const response = await api.get(`/tasks/count?${params.toString()}`);

      if (isMountedRef.current && response.data?.count !== undefined) {
        setTaskCount(response.data.count);
      }
    } catch (error) {}
  }, [user]);

  // WebSocket listener: update task count in real-time
  useEffect(() => {
    if (!socket || !user?.id) return;

    const handleTaskCount = (data) => {
      if (data.userId === user.id && isMountedRef.current) {
        setTaskCount(data.count);
      }
    };

    // Ask server to make sure we're in the right room (extra safety)
    socket.emit("join", { userId: user.id });
    socket.on("taskCountUpdated", handleTaskCount);

    // Also fetch once on connect as a fallback
    fetchTaskCount();

    return () => {
      socket.off("taskCountUpdated", handleTaskCount);
    };
  }, [socket, user?.id, fetchTaskCount]);

  // Temporary: Fetch initial count on mount
  useEffect(() => {
    if (user?.id) {
      fetchTaskCount();
    }
  }, [user?.id, fetchTaskCount]);

  // Fetch the unbilled-assignments count (previous months, not invoiced)
  const fetchUnbilledCount = useCallback(async () => {
    try {
      const response = await api.get("/invoices/unbilled-count");
      if (isMountedRef.current && response.data?.count !== undefined) {
        setUnbilledCount(response.data.count);
      }
    } catch (error) {}
  }, []);

  // WebSocket listener: refresh the unbilled badge when invoices/services change
  useEffect(() => {
    if (!socket) return;

    const handleUnbilledCount = () => {
      fetchUnbilledCount();
    };

    socket.on("unbilledCountUpdated", handleUnbilledCount);
    fetchUnbilledCount();

    return () => {
      socket.off("unbilledCountUpdated", handleUnbilledCount);
    };
  }, [socket, fetchUnbilledCount]);

  // Local state to control the actual collapse state with delay
  const [isCollapsed, setIsCollapsed] = useState(contextIsCollapsed);

  // State to control text fade animations
  const [textVisible, setTextVisible] = useState(!contextIsCollapsed);

  // State to prevent multiple clicks during animation
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle context collapse state changes with sequential animation
  useEffect(() => {
    // If we're already in the desired state, do nothing
    if (
      contextIsCollapsed === isCollapsed &&
      textVisible === !contextIsCollapsed
    ) {
      return;
    }

    setIsAnimating(true);

    if (contextIsCollapsed) {
      // Step 1: Fade out text
      setTextVisible(false);

      // Step 2: After text fade out completes, collapse drawer
      const timer = setTimeout(() => {
        setIsCollapsed(true);
        setIsAnimating(false);
      }, ANIMATION_SPEED); // Wait for text fade out

      return () => clearTimeout(timer);
    } else {
      // When expanding, first expand drawer
      setIsCollapsed(false);

      // Then fade in text after drawer expands
      const timer = setTimeout(() => {
        setTextVisible(true);
        setIsAnimating(false);
      }, ANIMATION_SPEED); // Wait for drawer expansion

      return () => clearTimeout(timer);
    }
  }, [contextIsCollapsed, isCollapsed, textVisible]);

  // Calculate the current drawer width based on collapsed state
  const currentDrawerWidth = isCollapsed ? collapsedDrawerWidth : drawerWidth;

  const navItems = [
    {
      text: "WORKSPACE",
      link: "",
      icon: null,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "Dashboard",
      link: "dashboard",
      params: "",
      icon: <DashboardRounded />,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "Calendar",
      link: "calendar",
      icon: <CalendarMonthRoundedIcon />, //CalendarMonthRoundedIcon
      role: ["Admin", "Member"],
    },
    {
      text: "Tasks",
      link: "tasks",
      icon: <FormatListBulletedRoundedIcon />,
      role: ["Admin", "Member"],
    },
    {
      text: "SERVICES",
      link: "services",
      icon: null,
      role: ["Admin"], // Clickable for Admin - navigates to All Services
    },
    {
      text: "SERVICES",
      link: "",
      icon: null,
      role: ["Member"], // Non-clickable header for Members
    },
    {
      text: "Interpretations",
      link: "interpretations",
      icon: <RecordVoiceOverRounded />,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "Translations",
      link: "translations",
      icon: <TranslateRounded />,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "Other Services",
      link: "otherServices",
      icon: <MoreHorizRoundedIcon />,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "CONNECTIONS",
      link: "",
      icon: null,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "Clients",
      link: "clients",
      icon: <PeopleRounded />,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "Linguists",
      link: "linguists",
      icon: <InterpreterModeRounded />,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "MANAGEMENT",
      link: "",
      icon: null,
      role: ["Admin"], // Admin only section
    },
    {
      text: "Linguists' Payments",
      link: "linguistsPayments",
      icon: <PriceCheckRounded />,
      role: ["Admin"], // Admin only
    },
    {
      text: "Expenses",
      link: "expenses",
      icon: <PointOfSaleRounded />,
      role: ["Admin"], // Admin only
    },
    {
      text: "Invoices",
      link: "invoices",
      icon: <RequestPageRounded />,
      role: ["Admin"], // Admin only
    },
    {
      text: "Reports",
      link: "reports",
      icon: <AssessmentRounded />,
      role: ["Admin"], // Admin only
    },
    {
      text: "SETTINGS",
      link: "",
      icon: null,
      role: ["Admin", "Member"], // Visible to all roles
    },
    {
      text: "Members",
      link: "members",
      icon: <GroupsRounded />,
      role: ["Admin"], // Admin only
    },
    {
      text: "Settings",
      link: "settings",
      icon: <SettingsRounded />,
      role: ["Admin", "Member"], // Visible to all roles
    },
    //{
    //  text: "Changelog",
    //  link: "changelog",
    //  icon: <PublishedWithChangesIcon />,
    //  role: ["Admin", "Member"], // Visible to all roles
    //},
    {
      text: "Logs",
      link: "logs",
      icon: <TerminalRoundedIcon />,
      role: ["Admin"], // Admin only — mirrors the old View Logs gate
    },
    {
      text: "Help",
      link: "help",
      icon: <HelpCenterRoundedIcon />,
      role: ["Admin", "Member"], // Visible to all roles
    },
  ];

  const { pathname } = useLocation();
  const [active, setActive] = useState("");
  const navigate = useNavigate();

  // Fixed toggle function that handles both collapsing and closing
  const handleToggleSidebar = () => {
    // Prevent multiple clicks during animation
    if (isAnimating) return;

    // On mobile: simply close the sidebar (single state behavior)
    if (!isNonMobile) {
      setActiveMenu(false);
      return;
    }

    // On desktop: toggle between open, collapsed, and expanded states
    if (!activeMenu) {
      // If sidebar is closed, open it fully
      setActiveMenu(true);
      setContextIsCollapsed(false);
    } else if (!contextIsCollapsed) {
      // If sidebar is fully open, collapse it
      setContextIsCollapsed(true);
    } else if (contextIsCollapsed) {
      // If sidebar is collapsed, expand it (not close it)
      setContextIsCollapsed(false);
    }
  };

  // Handle navigation and close sidebar on mobile
  const handleNavigation = (link, params) => {
    if (!link) return; // Don't navigate for section headers

    if (params) navigate(`/${link}`, { state: params });
    else navigate(`/${link}`);

    setActive(link);

    // Close sidebar on mobile
    if (!isNonMobile) {
      setActiveMenu(false);
    }
  };

  // Update active state based on the first segment of the url
  useEffect(() => {
    const pathParts = pathname.split("/");
    setActive(pathParts[1]);
  }, [pathname]);

  // Filter navItems based on user role and restrictedTo
  const filteredNavItems = navItems.filter((item) => {
    // If no role is specified or the user's role is included in the item's roles
    const roleMatch = !item.role || item.role.includes(user?.Role || "Member");

    // If restrictedTo is specified, check if user name matches
    const restrictedMatch =
      !item.restrictedTo || item.restrictedTo.includes(user?.Name);

    return roleMatch && restrictedMatch;
  });

  // Check if a section header has any visible children
  const sectionHasVisibleItems = (sectionText) => {
    const sectionIndex = filteredNavItems.findIndex(
      (item) => item.text === sectionText && !item.icon,
    );
    if (sectionIndex === -1) return false;

    // Look for the next section header or end of array
    let nextSectionIndex = filteredNavItems.findIndex(
      (item, index) => index > sectionIndex && !item.icon,
    );
    if (nextSectionIndex === -1) nextSectionIndex = filteredNavItems.length;

    // Check if there are any items between this section header and the next
    return nextSectionIndex > sectionIndex + 1;
  };

  return (
    <>
      <Box
        component="nav"
        sx={{
          width: activeMenu ? currentDrawerWidth : 0,
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 1200,
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.easeInOut,
              duration: ANIMATION_SPEED, // Animation for drawer width
            }),
        }}
      >
        {activeMenu && (
          <Drawer
            open={activeMenu}
            variant={!isNonMobile ? "temporary" : "permanent"}
            anchor="left"
            onClose={() => !isNonMobile && setActiveMenu(false)}
            PaperProps={{
              elevation: 10,
              sx: {
                width: currentDrawerWidth,
                border: "none",
                boxSizing: "border-box",
                transition: (theme) =>
                  theme.transitions.create("width", {
                    easing: theme.transitions.easing.easeInOut,
                    duration: ANIMATION_SPEED, // Animation for drawer width
                  }),
              },
            }}
            sx={{
              width: currentDrawerWidth,
              "& .MuiDrawer-paper": {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.background.default,
                width: currentDrawerWidth,
                // The paper matches the viewport; the nav list is the ONLY
                // scroll region (prevents an outer/paper-level scrollbar on
                // mobile, where viewport-height units overflow the visible area).
                overflow: "hidden",
              },
            }}
          >
            {/* Main container with flex layout */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
              }}
            >
              {/* Header section */}
              <Box
                p={isCollapsed ? 1 : 2}
                pb={isCollapsed ? 1 : 3}
                sx={{ flexShrink: 0 }}
              >
                <FlexBetween color={theme.palette.primary.main}>
                  {/* Logo with "CE Portal" text */}
                  {isCollapsed ? (
                    // Collapsed: Only logo, centered
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <img
                        src="/assets/ce-logo.svg"
                        alt="CE Portal Logo"
                        style={{
                          height: "32px",
                          width: "auto",
                          maxWidth: "150px",
                        }}
                      />
                    </Box>
                  ) : (
                    // Expanded: Logo with "CE Portal" text
                    <Box
                      sx={{
                        opacity: textVisible ? 1 : 0,
                        transition: `opacity ${ANIMATION_SPEED}ms ease-in-out`,
                        whiteSpace: "nowrap",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <img
                        src="/assets/ce-logo.svg"
                        alt="CE Portal Logo"
                        style={{
                          height: "32px",
                          width: "auto",
                          maxWidth: "150px",
                        }}
                      />

                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ color: theme.palette.primary.main }}
                      >
                        CE Portal
                      </Typography>
                    </Box>
                  )}
                </FlexBetween>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  px: isCollapsed ? 0 : 0,
                }}
              >
                {filteredNavItems.map((item, index) => {
                  // Skip section headers that don't have any visible items
                  if (!item.icon && !sectionHasVisibleItems(item.text)) {
                    return null;
                  }

                  // Section header
                  if (!item.icon) {
                    // If section header has a link, make it clickable
                    if (item.link) {
                      return (
                        <React.Fragment key={`section-${index}`}>
                          {isCollapsed &&
                            index > 0 &&
                            filteredNavItems[index - 1]?.icon && (
                              <Divider sx={{ my: 1, mx: 0.5 }} />
                            )}
                          <Box
                            onClick={() => handleNavigation(item.link)}
                            sx={{
                              mx: 2,
                              mt: 0.5,
                              mb: 0.125,
                              cursor: "pointer",
                              opacity: textVisible ? 1 : 0,
                              transition: `opacity ${ANIMATION_SPEED}ms ease-in-out`,
                              "&:hover": {
                                "& .MuiTypography-root": {
                                  color: theme.palette.primary.light,
                                },
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.primary.main,
                                fontWeight: "bold",
                                fontSize: 13,
                              }}
                            >
                              {item.text}
                            </Typography>
                          </Box>
                        </React.Fragment>
                      );
                    }
                    // Non-clickable section header
                    return (
                      <React.Fragment key={`section-${index}`}>
                        {isCollapsed &&
                          index > 0 &&
                          filteredNavItems[index - 1]?.icon && (
                            <Divider sx={{ my: 1, mx: 0.5 }} />
                          )}
                        <SectionHeader
                          text={item.text}
                          isCollapsed={isCollapsed}
                          textVisible={textVisible}
                        />
                      </React.Fragment>
                    );
                  }

                  // Menu item with icon
                  const menuItem = (
                    <MenuItem
                      key={item.text}
                      icon={item.icon}
                      text={item.text}
                      active={active}
                      link={item.link}
                      onClick={() => handleNavigation(item.link, item.params)}
                      isCollapsed={isCollapsed}
                      textVisible={textVisible}
                      badgeCount={
                        item.text === "Tasks"
                          ? taskCount
                          : item.text === "Invoices"
                            ? unbilledCount
                            : 0
                      }
                    />
                  );

                  // Wrap with HtmlTooltip in collapsed mode
                  return isCollapsed ? (
                    <HtmlTooltip
                      key={`tooltip-${item.text}`}
                      title={item.text}
                      placement="right"
                    >
                      <Box>{menuItem}</Box>
                    </HtmlTooltip>
                  ) : (
                    menuItem
                  );
                })}
              </Box>

              {/* Bottom navigation - will stay at the bottom */}
              <Box sx={{ flexShrink: 0 }}>
                <Divider sx={{ my: 1 }} />

                {isCollapsed ? (
                  // Collapsed view: Open Sidebar button
                  <Box sx={{ mx: 0.5, my: 0.5 }}>
                    {/* Open Sidebar Button */}
                    <HtmlTooltip title="Open Sidebar" placement="right">
                      <IconButton
                        onClick={handleToggleSidebar}
                        disabled={isAnimating}
                        sx={{
                          width: "100%",
                          borderRadius: "12px",
                          p: 1.5,
                          justifyContent: "center",
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.buttonHover,
                          },
                        }}
                      >
                        <MenuRoundedIcon />
                      </IconButton>
                    </HtmlTooltip>
                  </Box>
                ) : (
                  // Expanded view: Close Sidebar button
                  <Box
                    sx={{
                      display: "flex",
                      mx: 2,
                      my: 0.5,
                    }}
                  >
                    {/* Close Sidebar Button */}
                    <Box
                      sx={{
                        width: "100%",
                      }}
                    >
                      <HtmlTooltip title="Close Sidebar" placement="right">
                        <IconButton
                          onClick={() =>
                            isNonMobile
                              ? handleToggleSidebar()
                              : setActiveMenu(false)
                          }
                          disabled={isAnimating}
                          sx={{
                            width: "100%",
                            borderRadius: "12px",
                            p: 1.5,
                            justifyContent: "center",
                            color: theme.palette.primary.main,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.primary.buttonHover,
                            },
                          }}
                        >
                          <MenuOpenRoundedIcon />
                        </IconButton>
                      </HtmlTooltip>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Drawer>
        )}
      </Box>
    </>
  );
};

export default Sidebar;
